import { createUseStyles } from "react-jss";
import { foregroundColor, darken, alpha } from "../../theme/Mixins";
import * as InputTokens from "../../theme/inputThemeTokens";
import * as CardSortTokens from "../../theme/cardSortThemeTokens";
import type { Theme } from "../../types/Theme";

const isModernTheme = (theme: Theme) => theme.UseModernTheme === true;

export default createUseStyles({
    container: {
        position: "relative",
        userSelect: "none",
        width: "100%",
        boxSizing: "border-box",
    },
    group: {
        position: "absolute",
        textAlign: "center",
        fontWeight: "bolder",
    },
    bucket: {
        position: "absolute",
        background: ({ theme, color }: { theme: Theme; color?: string }) =>
            `${alpha(
                color || theme.ControlBackground,
                theme.ControlAlpha,
            )} 0% 0% no-repeat padding-box`,
        color: ({ theme, color }: { theme: Theme; color?: string }) =>
            foregroundColor(theme, color || theme.ControlBackground),
        border: ({ theme, color }: { theme: Theme; color?: string }) => [
            [
                theme.BorderWidth,
                "solid",
                darken(color || theme.ControlBackground, theme.FieldBorderValue),
            ],
        ],
        borderRadius: ({ theme }) => Math.min(theme.BorderRadius, 50),
        textAlign: "center",
        fontWeight: "bolder",
    },
    card: {
        backgroundColor: ({ theme, color }: { theme: Theme; color?: string }) =>
            color || theme.SecondaryColor,
        border: ({ theme, color }: { theme: Theme; color?: string }) => [
            [
                Math.max(theme.BorderWidth, 1),
                "solid",
                darken(color || theme.SecondaryColor, theme.FieldBorderValue),
            ],
        ],
        borderRadius: ({ theme }) => theme.BorderRadius,
        color: ({ theme, color }: { theme: Theme; color?: string }) =>
            foregroundColor(theme, color || theme.SecondaryColor),
        position: "absolute",
        textAlign: "center",
        fontWeight: "bolder",
        cursor: "grab",
        boxSizing: "border-box",

        "&:active": {
            cursor: "grabbing",
        },
    },
    bucketModern: {
        position: "absolute",
        boxSizing: "border-box",
        background: ({ theme, color }: { theme: Theme; color?: string }) =>
            alpha(color || theme.ControlBackground, theme.ControlAlpha),
        border: ({ theme, color }: { theme: Theme; color?: string }) =>
            color
                ? [[theme.BorderWidth, "solid", darken(color, theme.FieldBorderValue)]]
                : InputTokens.resolveTextInputBorder(theme),
        color: ({ theme, color }: { theme: Theme; color?: string }) =>
            color ? foregroundColor(theme, color) : undefined,
        borderRadius: ({ theme }: { theme: Theme }) =>
            CardSortTokens.resolveCardSortBucketBorderRadius(theme),
        padding: ({ theme }: { theme: Theme }) =>
            CardSortTokens.resolveCardSortBucketPadding(theme),
    },
    bucketTitleModern: {
        fontWeight: 700,
        fontSize: ({ theme }: { theme: Theme }) =>
            parseInt(String(theme.TextFontSize ?? theme.BaseFontSize), 10) || theme.BaseFontSize,
        textAlign: "start",
        lineHeight: 1.25,
        overflowWrap: "break-word",
        paddingLeft: ({ theme }: { theme: Theme }) =>
            CardSortTokens.resolveCardSortBucketTitlePaddingX(theme),
        paddingRight: ({ theme }: { theme: Theme }) =>
            CardSortTokens.resolveCardSortBucketTitlePaddingX(theme),
    },
    groupHeaderModern: {
        position: "absolute",
        display: "flex",
        alignItems: "center",
        gap: ({ theme }: { theme: Theme }) => theme.CardSortHeaderGap,
        boxSizing: "border-box",
    },
    groupLabelModern: {
        fontWeight: 700,
        fontSize: ({ theme }: { theme: Theme }) =>
            parseInt(String(theme.TextFontSize ?? theme.BaseFontSize), 10) || theme.BaseFontSize,
        textAlign: "start",
        lineHeight: 1.25,
        flexShrink: 0,
    },
    groupDividerModern: {
        flex: 1,
        height: 1,
        backgroundColor: ({ theme }: { theme: Theme }) =>
            CardSortTokens.resolveCardSortGroupDividerColor(theme),
    },
    cardModern: {
        position: "absolute",
        boxSizing: "border-box",
        background: ({ theme, color }: { theme: Theme; color?: string }) =>
            color || InputTokens.resolveTextInputBackground(theme),
        border: ({ theme, color }: { theme: Theme; color?: string }) =>
            color
                ? [
                      [
                          Math.max(theme.BorderWidth, 1),
                          "solid",
                          darken(color, theme.FieldBorderValue),
                      ],
                  ]
                : InputTokens.resolveTextInputBorder(theme),
        borderRadius: ({ theme }: { theme: Theme }) =>
            CardSortTokens.resolveCardSortCardBorderRadius(theme),
        color: ({ theme, color }: { theme: Theme; color?: string }) =>
            color
                ? foregroundColor(theme, color)
                : InputTokens.resolveTextInputForeground(theme),
        fontSize: ({ theme }: { theme: Theme }) =>
            parseInt(String(theme.TextFontSize ?? theme.BaseFontSize), 10) || theme.BaseFontSize,
        fontWeight: 400,
        textAlign: "start",
        lineHeight: 1.25,
        boxShadow: "none",
        transition: "box-shadow 150ms ease",
        cursor: "grab",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",

        "&:active": {
            cursor: "grabbing",
        },

        "&:hover": {
            boxShadow: ({ theme }: { theme: Theme }) =>
                CardSortTokens.resolveCardSortCardHoverBoxShadow(theme),
        },
    },
    cardContentModern: {
        width: "100%",
        boxSizing: "border-box",
        minWidth: 0,
        overflowWrap: "break-word",
        padding: ({ theme }: { theme: Theme }) => CardSortTokens.resolveCardSortCardPadding(theme),
    },
    cardImageModern: {
        padding: 0,
        overflow: "hidden",
        display: "block",
        alignItems: "unset",
        background: "transparent",
    },
    cardImageLegacy: {
        padding: 0,
        overflow: "hidden",
        display: "block",
        background: "transparent",
    },
    cardImage: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: "center",
        display: "block",
        pointerEvents: "none",
    },
    cardDragging: {
        background: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? theme.PrimaryColor : undefined,
        borderColor: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? theme.PrimaryColor : undefined,
        color: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? theme.WhiteColor : undefined,
    },
    cardDraggingImage: {
        borderColor: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? theme.PrimaryColor : undefined,
    },
    dropHereModern: {
        position: "absolute",
        boxSizing: "border-box",
        pointerEvents: "none",
        zIndex: 3,
        borderRadius: ({ theme }: { theme: Theme }) => theme.BorderRadius,
        border: ({ theme }: { theme: Theme }) => [
            [
                Math.max(theme.BorderWidth, 2),
                "dashed",
                CardSortTokens.resolveCardSortDropZoneBorderColor(theme),
            ],
        ],
        background: ({ theme }: { theme: Theme }) =>
            alpha(CardSortTokens.resolveCardSortDropZoneBorderColor(theme), 0.04),
    },
});
